import { useCallback, useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { UserContext } from '../../context';
import { StoryTabType } from '../models';
import { isAdminLayout, layoutPath } from '../utils';
import { useEventsFeed } from './useEventsFeed';
import { useNewsFeed } from './useNewsFeed';
import { usePinnedStoryId } from './usePinnedStoryId';
import { useSearchFeed } from './useSearchFeed';
import { useStoriesFeed } from './useStoriesFeed';
import { useStoryFeed } from './useStoryFeed';
import { useTagsFeed } from './useTagsFeed';
import { useUserFeed } from './useUserFeed';

interface RefreshFeedsProps {
  scrollTop?: boolean;
  storyTabType?: StoryTabType;
}

export const useRefreshFeeds = ({ scrollTop, storyTabType }: RefreshFeedsProps) => {
  const { id: userId } = useContext(UserContext).userInfo.userData;

  const { pathname } = useLocation();

  const { profileId, storyId, organisationTagId } = useParams();

  const {
    getNewsFeedItems,
    getPinnedFeedItems,
    pinnedStorySection,
    getSubTabFeedItems,
    subTabStoryId,
  } = useNewsFeed();

  const { getSearchFeedItems } = useSearchFeed();

  const { getUserFeedItems } = useUserFeed({ userId: Number(profileId ?? userId) });

  const { getEventsFeedItems } = useEventsFeed();

  const { getStoriesFeed } = useStoriesFeed({ storyTabType });

  const { getStoryFeedItems } = useStoryFeed({ storyId: Number(storyId), storyTabType });

  const { getTagsFeedItems } = useTagsFeed();

  const { getPinnedStoryId } = usePinnedStoryId();

  const refreshNewsFeed = useCallback(() => {
    const pinnedStoryId = getPinnedStoryId(pinnedStorySection);

    if (isAdminLayout() && pinnedStoryId) {
      return getPinnedFeedItems(pinnedStoryId);
    }

    if (subTabStoryId) {
      return getSubTabFeedItems();
    }

    return getNewsFeedItems();
  }, [
    getNewsFeedItems,
    getPinnedFeedItems,
    getPinnedStoryId,
    getSubTabFeedItems,
    pinnedStorySection,
    subTabStoryId,
  ]);

  const refreshFeeds = useCallback(() => {
    switch (pathname) {
      case layoutPath('/'): {
        scrollTop && window.scrollTo(0, 0);
        return refreshNewsFeed();
      }
      case layoutPath('/search'): {
        scrollTop && window.scrollTo(0, 0);
        return getSearchFeedItems();
      }
      case layoutPath(profileId ? `/profile/${profileId}` : '/profile'): {
        scrollTop && window.scrollTo(0, 0);
        return getUserFeedItems();
      }
      case '/admin/storyTab':
      case '/admin/storyTab1':
      case '/admin/storyTab2':
      case '/admin/storyTab3':
      case '/admin/storyTab4':
      case '/admin/storyTab5':
      case '/admin/pinnedStoryTab':
      case '/admin/pinnedStoryTab2':
      case '/admin/pinnedStoryTab3':
      case '/admin/pinnedStoryTab4':
      case '/admin/pinnedStoryTab5':
        return getStoriesFeed();
      case `/storyTab1/${storyId}/items`:
      case `/storyTab2/${storyId}/items`:
      case `/storyTab3/${storyId}/items`:
      case `/storyTab4/${storyId}/items`:
      case `/storyTab5/${storyId}/items`:
      case `/pinnedStoryTab/${storyId}/items`:
      case `/pinnedStoryTab2/${storyId}/items`:
      case `/pinnedStoryTab3/${storyId}/items`:
      case `/pinnedStoryTab4/${storyId}/items`:
      case `/pinnedStoryTab5/${storyId}/items`:
      case `/admin/storyTab/${storyId}/items`:
      case `/admin/storyTab1/${storyId}/items`:
      case `/admin/storyTab2/${storyId}/items`:
      case `/admin/storyTab3/${storyId}/items`:
      case `/admin/storyTab4/${storyId}/items`:
      case `/admin/storyTab5/${storyId}/items`:
      case `/admin/pinnedStoryTab/${storyId}/items`:
      case `/admin/pinnedStoryTab2/${storyId}/items`:
      case `/admin/pinnedStoryTab3/${storyId}/items`:
      case `/admin/pinnedStoryTab4/${storyId}/items`:
      case `/admin/pinnedStoryTab5/${storyId}/items`:
        return getStoryFeedItems();
      case layoutPath('/events'):
        return getEventsFeedItems();
      case layoutPath(`/tags/${organisationTagId}`):
        return getTagsFeedItems();
    }
  }, [
    getEventsFeedItems,
    getSearchFeedItems,
    getStoriesFeed,
    getStoryFeedItems,
    getTagsFeedItems,
    getUserFeedItems,
    organisationTagId,
    pathname,
    profileId,
    refreshNewsFeed,
    scrollTop,
    storyId,
  ]);

  return { refreshFeeds };
};
